import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SalesOrderLineItemDto } from '../types/salesOrder.types';

// Forked from QuotationBulkEditDialog.

const UNCHANGED = '__unchanged__';

// Editable spec option lists. Mirrors the quotation-side EDITABLE_* arrays
// in shape; tailored here so we don't reach across into the quotations
// feature.
const EDITABLE_DIAMETERS_MM = [6, 8, 10, 12, 16, 20, 25, 28, 32, 36, 40, 50];
const EDITABLE_LENGTHS_M = [2, 3, 4, 5, 6, 7.5, 9, 10.5, 12, 13.5, 15, 18];
// G33 excluded — not a sellable grade (auto-promoted to G40; no base markup). 86d3j5qca.
const EDITABLE_GRADES = ['40', '60', '75', '80'];
// Mirrors PRICE_LEVEL_OPTIONS in SalesOrderLineItemsTable — Base/Average only
// (the levels FO grade lines use, the only ones with a sourceable item price).
const PRICE_LEVEL_OPTIONS = [
  { value: '1', label: 'Base Price' },
  { value: '2', label: 'Average Price' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  onApply: (patch: Partial<SalesOrderLineItemDto>) => void;
}

interface FormState {
  diameter: string;
  length: string;
  grade: string;
  weldable: string;
  priceLevel: string;
  quantity: string;
}

const EMPTY: FormState = {
  diameter: UNCHANGED,
  length: UNCHANGED,
  grade: UNCHANGED,
  weldable: UNCHANGED,
  priceLevel: UNCHANGED,
  quantity: '',
};

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring';

export function SalesOrderBulkEditDialog({ open, onOpenChange, selectedCount, onApply }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const patch = buildPatch(form);
  const hasChanges = Object.keys(patch).length > 0;

  function handleApply() {
    if (!hasChanges) return;
    onApply(patch);
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Bulk edit {selectedCount} line item{selectedCount === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Only the fields you change are applied. Leave a field as
            <span className="mx-1 font-medium">— unchanged —</span>
            to skip it.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <Field label="Diameter">
            <BulkSelect value={form.diameter} onValueChange={(v) => setForm((f) => ({ ...f, diameter: v }))}>
              {EDITABLE_DIAMETERS_MM.map((d) => (
                <SelectItem key={d} value={String(d)}>
                  {d} mm
                </SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Length">
            <BulkSelect value={form.length} onValueChange={(v) => setForm((f) => ({ ...f, length: v }))}>
              {EDITABLE_LENGTHS_M.map((l) => (
                <SelectItem key={l} value={String(l)}>
                  {l} m
                </SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Grade">
            <BulkSelect value={form.grade} onValueChange={(v) => setForm((f) => ({ ...f, grade: v }))}>
              {EDITABLE_GRADES.map((g) => (
                <SelectItem key={g} value={g}>
                  G{g}
                </SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Weldable">
            <BulkSelect value={form.weldable} onValueChange={(v) => setForm((f) => ({ ...f, weldable: v }))}>
              <SelectItem value="Y">Y</SelectItem>
              <SelectItem value="N">N</SelectItem>
            </BulkSelect>
          </Field>

          <Field label="Price Level">
            <BulkSelect value={form.priceLevel} onValueChange={(v) => setForm((f) => ({ ...f, priceLevel: v }))}>
              {PRICE_LEVEL_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </BulkSelect>
          </Field>

          <Field label="Qty">
            <input
              type="number"
              inputMode="decimal"
              min={1}
              step={1}
              className={inputCls}
              value={form.quantity}
              placeholder="— unchanged —"
              onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            />
          </Field>

        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply} disabled={!hasChanges}>
            Apply to {selectedCount} row{selectedCount === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Themed Select for the bulk-edit fields (86d3c38ve). The "— unchanged —" row is
// the always-present first option; since `value` is never empty (defaults to
// UNCHANGED) the trigger renders the selected item's own text — no placeholder.
function BulkSelect({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNCHANGED}>— unchanged —</SelectItem>
        {children}
      </SelectContent>
    </Select>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[100px_1fr] items-center gap-3">
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div>{children}</div>
    </div>
  );
}

function buildPatch(form: FormState): Partial<SalesOrderLineItemDto> {
  const patch: Partial<SalesOrderLineItemDto> = {};
  if (form.diameter !== UNCHANGED) patch.diameterMm = Number(form.diameter);
  if (form.length !== UNCHANGED) patch.lengthM = Number(form.length);
  if (form.grade !== UNCHANGED) patch.gradeCode = form.grade;
  if (form.weldable !== UNCHANGED) patch.isWeldable = form.weldable === 'Y';
  if (form.priceLevel !== UNCHANGED) patch.priceLevel = form.priceLevel;
  if (form.quantity.trim() !== '') {
    const q = Number(form.quantity);
    if (!Number.isNaN(q)) patch.quantity = q;
  }
  return patch;
}
